import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Link,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  TablePagination,
  TableSortLabel
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import LaunchIcon from "@material-ui/icons/Launch";
import DescriptionIcon from "@material-ui/icons/Description";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import FinDetailsIcon from "../../../../../components/svgComponents/FinDetailsIcon";
import { Link as RouterLink } from "react-router-dom";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import ruLocale from "date-fns/locale/ru";
import { getAllFinDataRequests, downloadFile } from "../../../../../../dao/api";
import DownloadArchiveIcon from "../../../../../components/svgComponents/DownloadArchiveIcon";
import { formatDate } from "../../../../../../localization/utils";
import FinDataRequestModal from "../../../request/FinDataRequestModal";
import HtmlAccountEditorModal from "../HtmlAccountEditorModal";
import AdminTable from "../AdminTable";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 0,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: "24px",
    fontWeight: 600,
    color: "#1A1E24",
  },
  filtersContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#F8F9FA",
    borderRadius: "8px",
    border: "1px solid #E1E6F1",
  },
  filterRow: {
    marginBottom: theme.spacing(2),
  },
  searchField: {
    minWidth: 200,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      "& fieldset": { borderColor: "#E1E6F1" },
      "&:hover fieldset": { borderColor: "#54606F" },
      "&.Mui-focused fieldset": { borderColor: "#54606F" },
    },
  },
  dateField: {
    minWidth: 120,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      "& fieldset": { borderColor: "#E1E6F1" },
      "&:hover fieldset": { borderColor: "#54606F" },
      "&.Mui-focused fieldset": { borderColor: "#54606F" },
    },
  },
  noData: {
    textAlign: "center",
    color: "#54606F",
    padding: theme.spacing(4),
    fontSize: "16px",
  },
  link: {
    color: "#5195E6",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  statusChip: {
    fontSize: "12px",
    height: "24px",
  },
  statusNew: {
    backgroundColor: "#E9F2FC",
    color: "#5195E6",
  },
  statusSentByClient: {
    backgroundColor: "#E9F5EE",
    color: "#43A56C",
  },
  statusCompleted: {
    backgroundColor: "#E9F5EE",
    color: "#43A56C",
  },
  statusDefault: {
    backgroundColor: "#F3F5F7",
    color: "#54606F",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

const AllRequestsTab = () => {
  const classes = useStyles();

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [accountEditorModalOpen, setAccountEditorModalOpen] = useState(false);
  const [selectedRequestForEditor, setSelectedRequestForEditor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    const loadRequests = async () => {
      try {
        const data = await getAllFinDataRequests();
        setRequests(data || []);
      } catch (error) {
        console.error("Error loading requests:", error);
      }
    };

    loadRequests();
  }, []);

  const filterRequests = (requests, searchTerm, startDate, endDate) => {
    return requests.filter(request => {
      // Text search filter
      const textMatch = !searchTerm ||
        request.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.clientName?.toLowerCase().includes(searchTerm.toLowerCase());

      // Date filter
      let dateMatch = true;
      if (startDate || endDate) {
        const requestStart = new Date(request.startDate);
        const requestEnd = new Date(request.endDate);

        if (startDate && endDate) {
          dateMatch = requestStart <= new Date(endDate) && requestEnd >= new Date(startDate);
        } else if (startDate) {
          dateMatch = requestEnd >= new Date(startDate);
        } else if (endDate) {
          dateMatch = requestStart <= new Date(endDate);
        }
      }

      return textMatch && dateMatch;
    });
  };

  const filteredRequests = filterRequests(requests, searchTerm, startDate, endDate);

  // Sorting
  const sortedRequests = React.useMemo(() => {
    if (!orderBy) return filteredRequests;

    const sorted = [...filteredRequests].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'customerName':
          aValue = a.customerName || '';
          bValue = b.customerName || '';
          break;
        case 'clientName':
          aValue = a.clientName || '';
          bValue = b.clientName || '';
          break;
        case 'startDate':
          aValue = new Date(a.startDate);
          bValue = new Date(b.startDate);
          break;
        case 'endDate':
          aValue = new Date(a.endDate);
          bValue = new Date(b.endDate);
          break;
        case 'updatedAt':
          aValue = new Date(a.updatedAt);
          bValue = new Date(b.updatedAt);
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredRequests, orderBy, order]);

  // Pagination
  const paginatedRequests = sortedRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, startDate, endDate]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'New':
        return classes.statusNew;
      case 'SentByClient':
        return classes.statusSentByClient;
      case 'Completed':
        return classes.statusCompleted;
      default:
        return classes.statusDefault;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'New':
        return 'Новый';
      case 'SentByClient':
        return 'Отправлен клиентом';
      case 'Completed':
        return 'Завершен';
      default:
        return status;
    }
  };

  const handleFinDetailsClick = (request) => {
    setSelectedRequest(request);
  };

  const handleCloseModal = () => {
    setSelectedRequest(null);
  };

  const handleAccountEditorClick = (request) => {
    setSelectedRequestForEditor(request);
    setAccountEditorModalOpen(true);
  };

  const handleCloseAccountEditor = () => {
    setAccountEditorModalOpen(false);
    setSelectedRequestForEditor(null);
  };

  return (
    <Box className={classes.container}>

      {/* Filters */}
      <Box className={classes.filtersContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              className={classes.searchField}
              placeholder="Поиск по заказчику или клиенту..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ruLocale}>
              <KeyboardDatePicker
                className={classes.dateField}
                label="Начальная дата"
                inputVariant="outlined"
                size="small"
                value={startDate}
                onChange={setStartDate}
                format="dd.MM.yyyy"
                clearable
                fullWidth
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12} md={2}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ruLocale}>
              <KeyboardDatePicker
                className={classes.dateField}
                label="Конечная дата"
                inputVariant="outlined"
                size="small"
                value={endDate}
                onChange={setEndDate}
                format="dd.MM.yyyy"
                clearable
                fullWidth
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12} md={5}>
            {sortedRequests.length > 0 && (
              <Box style={{ display: "flex", justifyContent: "flex-end" }}>
                <TablePagination
                  component="div"
                  count={sortedRequests.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 50, 100, 200]}
                  labelRowsPerPage="Строк на странице:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} из ${count !== -1 ? count : `более чем ${to}`}`
                  }
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <AdminTable>
        <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={orderBy === 'customerName'}
                  direction={orderBy === 'customerName' ? order : 'asc'}
                  onClick={() => handleRequestSort('customerName')}
                >
                  Заказчик
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={orderBy === 'clientName'}
                  direction={orderBy === 'clientName' ? order : 'asc'}
                  onClick={() => handleRequestSort('clientName')}
                >
                  Клиент
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={orderBy === 'startDate'}
                  direction={orderBy === 'startDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('startDate')}
                >
                  Начальная дата
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={orderBy === 'endDate'}
                  direction={orderBy === 'endDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('endDate')}
                >
                  Конечная дата
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Последнее обновление
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Статус
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRequests.length > 0 ? (
              paginatedRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell className={classes.tableCell}>
                    <Link
                      component={RouterLink}
                      to={`/customer/details/${request.customerId}`}
                      className={classes.link}
                    >
                      {request.customerName || "-"}
                    </Link>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Link
                      component={RouterLink}
                      to={`/client/details/${request.clientId}`}
                      className={classes.link}
                    >
                      {request.clientName || "-"}
                    </Link>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {request.formatedStartDate || formatDate(request.startDate)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {request.formatedEndDate || formatDate(request.endDate)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {request.formatedUpdatedAt || formatDate(request.updatedAt)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Chip
                      label={getStatusLabel(request.status)}
                      className={`${classes.statusChip} ${getStatusChipClass(request.status)}`}
                      size="small"
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box className={classes.actionButtons}>
                      <Tooltip title="Редактор счетов">
                        <IconButton
                          size="small"
                          onClick={() => handleAccountEditorClick(request)}
                        >
                          <AutoStoriesIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Финансовый отчет">
                        <IconButton
                          size="small"
                          onClick={() => handleFinDetailsClick({ ...request, clientId: request.clientId })}
                        >
                          <FinDetailsIcon />
                        </IconButton>
                      </Tooltip>
                      {request.hasDocuments && (
                        <Tooltip title="Скачать оригинальные файлы">
                          <IconButton
                            size="small"
                            onClick={() => downloadFile(`/api/FinDataRequest/${request.id}/documents/archive`)}
                          >
                            <DownloadArchiveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Скачать файл с расшифровками">
                        <IconButton
                          size="small"
                          onClick={() => downloadFile(`/api/FinDataRequest/${request.id}/file/transcriptions`)}
                        >
                          <DescriptionIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Перейти к запросу">
                        <IconButton
                          component={RouterLink}
                          to={`/findatarequest/${request.id}/data`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className={classes.noData}>
                  {searchTerm || startDate || endDate ? "Запросы не найдены" : "Нет данных о запросах"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
      </AdminTable>

      {/* Modal for financial report */}
      {selectedRequest && (
        <FinDataRequestModal
          openRequest={selectedRequest}
          handleOpen={() => { }}
          handleClose={handleCloseModal}
        />
      )}

      {/* Modal for HTML Account Editor */}
      {accountEditorModalOpen && selectedRequestForEditor && (
        <HtmlAccountEditorModal
          open={accountEditorModalOpen}
          onClose={handleCloseAccountEditor}
          requestId={selectedRequestForEditor.id}
        />
      )}
    </Box>
  );
};

export default AllRequestsTab;